"use client";

import { useMemo, useEffect, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Group, Button, ActionIcon, Skeleton, Box as MantineBox } from "@mantine/core";
import { useGlobalData } from "@/context/globalContext";
import { useAuthContext } from "@/context/authContext";
import { useFiltersStore } from "@/stores/filtersStore";
import { typeSelection, countrySelection } from "@/lib/shared/constants";
import ReshuffleIcon from "@/icons/Reshuffle";
import styles from "./styles.module.scss";

type PillFilterType = "brand" | "vendor" | "type" | "region" | "country";

type PillData =
  | { type: "brand" | "vendor" | "type" | "region"; filter: Filter; id: string }
  | { type: "saved"; savedFilter: DBSavedFilter; id: string };

interface SearchPillsProps {
  onPillClick: (type: PillFilterType, filter: Filter) => void;
  selectedInSearchBox?: Array<{ key: string; value: string | number }>;
  hideActiveFilters?: boolean;
}

// Predefined brand names for non-signed-in users
const DEFAULT_BRAND_NAMES = [
  "Maison Martin Margiela",
  "5351 Pour les Hommes",
  "Raf Simons",
  "Rick Owens",
  "Balenciaga",
  "Bernhard Willhelm",
  "Cop Copine",
  "Number (N)ine",
  "Helmut Lang",
  "Beauty:Beast",
  "Dior Homme",
  "Ann Demeulemeester",
  "If Six Was Nine",
  "Ne-net",
  "Craig Morrison",
  "Guidi",
  "m.a+",
  "Yohji Yamamoto",
  "Saint Laurent",
  "Swear London",
  "Algonquins",
  "Carol Christian Poell",
  "Yves Saint Laurent",
];

const PILL_COUNTRY_NAMES = [
  "United States",
  "Sweden",
  "Germany",
  "Canada",
  "Australia",
  "United Kingdom",
  "Japan",
];

const FILTER_TYPE_TO_KEY: Record<PillFilterType, string> = {
  brand: "brands",
  vendor: "vendors",
  type: "type",
  region: "regions",
  country: "countries",
};

const PILL_CONFIG = {
  total: 10,
  default: { brands: 4, vendors: 3, types: 2, countries: 1 },
  ratios: { brands: 0.4, vendors: 0.3, types: 0.2, countries: 0.1 },
};

const ANIMATION_DURATION_MS = 300;

// Helper to shuffle an array with mount-persistent randomization
const shuffleArray = <T,>(array: T[], seed: number): T[] => {
  const shuffled = [...array];
  let currentIndex = shuffled.length;

  let seedValue = seed * 1000000;
  const random = () => {
    seedValue = (seedValue * 9301 + 49297) % 233280;
    return seedValue / 233280;
  };

  while (currentIndex !== 0) {
    const randomIndex = Math.floor(random() * currentIndex);
    currentIndex--;
    [shuffled[currentIndex], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[currentIndex],
    ];
  }

  return shuffled;
};

// Helper to get a random item from an array
const getRandomItem = <T,>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)];
};

// Calculate pill counts based on remaining slots
const calculatePillCounts = (
  remainingSlots: number,
  ratios: { brands: number; vendors: number; types: number; countries: number }
) => {
  const brands = Math.min(Math.ceil(remainingSlots * ratios.brands), remainingSlots);
  const vendors = Math.min(Math.ceil(remainingSlots * ratios.vendors), remainingSlots - brands);
  const types = Math.min(
    Math.ceil(remainingSlots * ratios.types),
    remainingSlots - brands - vendors
  );
  const countries = remainingSlots - brands - vendors - types;

  return { brands, vendors, types, countries };
};

// Helper functions for generating pill data
const generateBrandPills = (
  brands: BrandFilter[],
  count: number,
  seed: number
): Array<{ type: "brand"; filter: Filter }> => {
  const shuffledNames = shuffleArray(DEFAULT_BRAND_NAMES, seed);
  const selectedNames = shuffledNames.slice(0, count);
  return selectedNames
    .map((name) => {
      const brand = brands.find((b) => b.label.toLowerCase() === name.toLowerCase());
      return brand ? { type: "brand" as const, filter: brand } : null;
    })
    .filter((p): p is { type: "brand"; filter: BrandFilter } => p !== null);
};

const generateVendorPills = (
  vendors: Vendor[],
  count: number,
  seed: number
): Array<{ type: "vendor"; filter: Filter }> => {
  const selectedVendors = shuffleArray(vendors, seed)
    .slice(0, count)
    .map((v) => ({ id: v.id, value: v.id, label: v.label }));
  return selectedVendors.map((vendor) => ({ type: "vendor" as const, filter: vendor }));
};

const generateTypePills = (
  count: number,
  seed: number
): Array<{ type: "type"; filter: Filter }> => {
  const selectedTypes = shuffleArray(typeSelection, seed).slice(0, count);
  return selectedTypes.map((type) => ({ type: "type" as const, filter: type }));
};

const generateCountryPills = (
  count: number,
  seed: number
): Array<{ type: "region"; filter: Filter }> => {
  const availableCountries = countrySelection.filter((c) => PILL_COUNTRY_NAMES.includes(c.label));
  const selectedCountries = shuffleArray(availableCountries, seed).slice(0, count);
  return selectedCountries.map((country) => ({ type: "region" as const, filter: country }));
};

const SearchPills = ({
  onPillClick,
  selectedInSearchBox = [],
  hideActiveFilters = false,
}: SearchPillsProps) => {
  const router = useRouter();
  const { brands, vendors } = useGlobalData();
  const { userId } = useAuthContext();
  const savedFilters = useFiltersStore((state) => state.savedFilters);
  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const selectSavedFilter = useFiltersStore((state) => state.actions.selectSavedFilter);
  const marketplaceVendors = useMemo(
    () => vendors?.filter((vendor) => vendor.type === 2) || [],
    [vendors]
  );
  const [usedFilters, setUsedFilters] = useState<Set<string>>(new Set());
  const [dynamicPills, setDynamicPills] = useState<PillData[]>([]);
  const [animatingIndex, setAnimatingIndex] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Only track savedFilters changes when user is signed in
  const savedFiltersForPills = userId ? savedFilters : undefined;

  const isDataReady = useMemo(() => {
    if (!brands || marketplaceVendors.length === 0) return false;
    return true;
  }, [brands, marketplaceVendors.length]);

  // Check if all pills are saved/preset filters
  const hasOnlyPresetFilters = useMemo(() => {
    return dynamicPills.length > 0 && dynamicPills.every((pill) => pill.type === "saved");
  }, [dynamicPills]);

  // Helper function to check if a specific filter is already selected
  const isFilterSelected = useCallback(
    (type: PillFilterType, filter: Filter) => {
      // First check if it's in the search box (for landing page)
      if (selectedInSearchBox.length > 0) {
        const isInSearchBox = selectedInSearchBox.some(
          (f) => f.key === type && f.value === filter.value
        );
        if (isInSearchBox) return true;
      }
    },
    [selectedInSearchBox]
  );

  const isInActiveFilters = useCallback(
    (type: PillFilterType, filter: Filter): boolean => {
      if (!activeFilters) return false;
      // Then check activeFilters (for search page)
      if (!activeFilters) return false;

      const filterKey = FILTER_TYPE_TO_KEY[type];
      if (!filterKey) return false;

      const filtersArray = activeFilters[filterKey];

      // Ensure filtersArray exists and is actually an array
      if (!filtersArray || !Array.isArray(filtersArray)) return false;

      // Check if this specific filter is in the active filters
      return filtersArray.some((f: Filter) => {
        // Handle both numeric and string comparisons
        return String(f.value) === String(filter.value);
      });
    },
    [activeFilters]
  );

  // Get available filters for a given type
  const getAvailableFiltersForType = useCallback(
    (type: "brand" | "vendor" | "type" | "region"): Filter[] => {
      let filters: Filter[];

      switch (type) {
        case "brand":
          filters =
            brands?.filter((b) =>
              DEFAULT_BRAND_NAMES.some((name) => name.toLowerCase() === b.label.toLowerCase())
            ) || [];
          break;
        case "vendor":
          filters = marketplaceVendors.map((v) => ({
            id: v.id,
            value: v.id,
            label: v.label,
          }));
          break;
        case "type":
          filters = typeSelection;
          break;
        case "region":
          filters = countrySelection.filter((c) => PILL_COUNTRY_NAMES.includes(c.label));
          break;
        default:
          filters = [];
      }

      // Filter out active filters when hideActiveFilters is true
      if (hideActiveFilters) {
        filters = filters.filter((filter) => !isInActiveFilters(type, filter));
      }

      return filters;
    },
    [brands, marketplaceVendors, hideActiveFilters, isInActiveFilters]
  );

  // Generate a new random pill of a specific type, avoiding already used ones
  const generateNewPillOfType = useCallback(
    (
      type: "brand" | "vendor" | "type" | "region"
    ): { type: "brand" | "vendor" | "type" | "region"; filter: Filter; id: string } | null => {
      if (!brands || marketplaceVendors.length === 0) return null;

      const availableFilters = getAvailableFiltersForType(type);
      if (availableFilters.length === 0) return null;

      // Try to find unused filters first
      const unusedFilters = availableFilters.filter((f) => !usedFilters.has(`${type}-${f.value}`));
      const filter = getRandomItem(unusedFilters.length > 0 ? unusedFilters : availableFilters);

      return { type, filter, id: `${type}-${filter.value}-${Date.now()}-${Math.random()}` };
    },
    [brands, marketplaceVendors.length, usedFilters, getAvailableFiltersForType]
  );

  // Generate initial pills
  const generatePills = useCallback(
    (seed: number) => {
      if (!brands || !marketplaceVendors.length) return [];

      const config = PILL_CONFIG;
      const pills: PillData[] = [];

      if (userId && savedFiltersForPills && savedFiltersForPills.length > 0) {
        const savedToPills = savedFiltersForPills.slice(0, config.total);
        savedToPills.forEach((savedFilter) => {
          pills.push({
            type: "saved",
            savedFilter,
            id: `saved-${savedFilter.id}`,
          });
        });

        if (pills.length >= config.total) {
          return pills;
        }
      }

      const remainingSlots = config.total - pills.length;
      const counts =
        remainingSlots === config.total
          ? config.default
          : calculatePillCounts(remainingSlots, config.ratios);

      const regularPills = [
        ...generateBrandPills(brands, counts.brands, seed),
        ...generateVendorPills(marketplaceVendors as unknown as Vendor[], counts.vendors, seed),
        ...generateTypePills(counts.types, seed),
        ...generateCountryPills(counts.countries, seed),
      ].map((pill) => ({
        ...pill,
        id: `${pill.type}-${pill.filter.value}-${seed}`,
      }));

      const shuffledRegularPills = shuffleArray(regularPills, seed);
      pills.push(...shuffledRegularPills);

      return pills;
    },
    [brands, marketplaceVendors, userId, savedFiltersForPills]
  );

  const loadNewPills = useCallback(() => {
    if (!brands || !marketplaceVendors.length) return;

    const seed = Date.now();
    const pills = generatePills(seed);

    setDynamicPills(pills);

    const newUsedFilters = new Set<string>();
    pills.forEach((pill) => {
      if (pill.type !== "saved") {
        newUsedFilters.add(`${pill.type}-${pill.filter.value}`);
      }
    });
    setUsedFilters(newUsedFilters);
  }, [brands, marketplaceVendors.length, generatePills]);

  useEffect(() => {
    if (!isDataReady || !brands || !marketplaceVendors.length) {
      return;
    }

    if (!isInitialized || (userId && savedFiltersForPills)) {
      loadNewPills();
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [
    isDataReady,
    isInitialized,
    brands,
    marketplaceVendors.length,
    generatePills,
    loadNewPills,
    userId,
    savedFiltersForPills,
  ]);

  const handleSavedFilterClick = useCallback(
    (savedFilter: DBSavedFilter) => {
      selectSavedFilter(savedFilter.id, true, savedFilters);
      router.push("/search");
    },
    [selectSavedFilter, savedFilters, router]
  );

  const handleRegularPillClick = useCallback(
    (pill: PillData & { type: "brand" | "vendor" | "type" | "region" }, index: number) => {
      onPillClick(pill.type, pill.filter);
      setAnimatingIndex(index);

      const newPill = generateNewPillOfType(pill.type);
      if (!newPill) return;

      // Wait for pop animation to complete before replacing
      setTimeout(() => {
        setDynamicPills((prev) => {
          const updated = [...prev];
          updated[index] = newPill;
          return updated;
        });

        setAnimatingIndex(null);
        setUsedFilters((prev) => new Set(prev).add(`${newPill.type}-${newPill.filter.value}`));
      }, ANIMATION_DURATION_MS);
    },
    [onPillClick, generateNewPillOfType]
  );

  const handlePillClick = useCallback(
    (pill: PillData, index: number) => {
      if (pill.type === "saved") {
        handleSavedFilterClick(pill.savedFilter);
      } else {
        handleRegularPillClick(pill, index);
      }
    },
    [handleSavedFilterClick, handleRegularPillClick]
  );

  if (!isDataReady || dynamicPills.length === 0) {
    return (
      <MantineBox className={styles.pillsContainer}>
        <Group gap="sm" justify="center" className={styles.pillRail}>
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton key={index} height={40} width={100} radius="xl" />
          ))}
          <Skeleton height={44} width={44} radius="xl" />
        </Group>
      </MantineBox>
    );
  }

  return (
    <MantineBox className={styles.pillsContainer}>
      <Group gap="sm" justify="center" className={styles.pillRail}>
        {dynamicPills.map((pill, index) => {
          const isPreset = pill.type === "saved";
          const isSelected = !isPreset && isFilterSelected(pill.type, pill.filter);
          const isAnimating = animatingIndex === index;

          return (
            <Button
              key={pill.id}
              variant={isPreset ? "pill-filled" : "pill"}
              className={`${styles.pillButton} ${isSelected ? styles.disabledPill : ""} ${isAnimating ? styles.bubblePop : ""}`}
              onClick={() => handlePillClick(pill, index)}
              disabled={isSelected || isAnimating}
              size="xs"
              style={{ textTransform: "none" }}
            >
              {pill.type === "saved" ? pill.savedFilter.search_filter.name : pill.filter.label}
            </Button>
          );
        })}
        {!hasOnlyPresetFilters && (
          <ActionIcon
            variant="transparent"
            onClick={loadNewPills}
            aria-label="Reshuffle suggestions"
            className={styles.reshuffleButton}
            size={44}
          >
            <ReshuffleIcon size={24} />
          </ActionIcon>
        )}
      </Group>
    </MantineBox>
  );
};

export default SearchPills;
